import { useRef, useEffect } from "react";
import styles from "./Background.module.scss";

type Vec2 = { x: number; y: number };

type FormationMap = {
  [key: string]: Vec2[];
};

const FORMATIONS: FormationMap = {
  two_ship: [
    { x: -10, y: 0 },
    { x: 10, y: 0 },
  ],
  three_ship: [
    { x: 0, y: 0 },
    { x: -10, y: 8 },
    { x: 10, y: 8 },
  ],
  four_ship: [
    { x: 0, y: 0 },
    { x: -10, y: 8 },
    { x: 10, y: 8 },
    { x: 0, y: 16 },
  ],
  offset_three: [
    { x: 0, y: 0 },
    { x: 12, y: 10 },
    { x: 5, y: 20 },
  ],
  echelon_right: [
    { x: 0, y: 0 },
    { x: 10, y: 10 },
    { x: 20, y: 20 },
    { x: 30, y: 30 },
  ],
  echelon_left: [
    { x: 0, y: 0 },
    { x: -10, y: 10 },
    { x: -20, y: 20 },
    { x: -30, y: 30 },
  ],
  box: [
    { x: -10, y: -10 },
    { x: 10, y: -10 },
    { x: -10, y: 10 },
    { x: 10, y: 10 },
  ],
  trail: [
    { x: 0, y: 0 },
    { x: 0, y: 12 },
    { x: 0, y: 24 },
    { x: 0, y: 36 },
  ],
  staggered_column: [
    { x: 0, y: 0 },
    { x: 6, y: 10 },
    { x: -6, y: 20 },
    { x: 6, y: 30 },
    { x: -6, y: 40 },
  ],
  loose_line: [
    { x: -20, y: 0 },
    { x: -5, y: 5 },
    { x: 10, y: -2 },
    { x: 25, y: 3 },
  ],
  vee_misaligned: [
    { x: 0, y: 0 },
    { x: -10, y: 10 },
    { x: 8, y: 13 },
  ],
  five_finger: [
    { x: 0, y: 0 },
    { x: -12, y: 6 },
    { x: 12, y: 6 },
    { x: -20, y: 12 },
    { x: 20, y: 12 },
  ],
};

const getRandomFormation = (): keyof typeof FORMATIONS => {
  const keys = Object.keys(FORMATIONS) as (keyof typeof FORMATIONS)[];
  return keys[Math.floor(Math.random() * keys.length)];
};

type FormationInstance = {
  type: keyof typeof FORMATIONS;
  units: Vec2[];
  center: Vec2;
  radius: number;
  angle: number;
  speed: number;
  color: string;
};

const Background = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const getRadarOrigin = (): Vec2 => ({ x: 0, y: canvas.height });

    const getMaxRadarRadius = (): number => {
      const { width, height } = canvas;
      return Math.sqrt(width * width + height * height);
    };

    const formationCount = 6;
    const formations: FormationInstance[] = Array.from({ length: formationCount }, () => {
      const type = getRandomFormation();
      const shape = FORMATIONS[type];

      return {
        type,
        units: shape,
        center: {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
        },
        radius: 60 + Math.random() * 180,
        angle: Math.random() * Math.PI * 2,
        speed: 0.0015 + Math.random() * 0.0025,
        color: Math.random() > 0.5 ? "#1fff87" : "#ff4c4c",
      };
    });

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const origin = getRadarOrigin();
      const maxRadius = getMaxRadarRadius();
      const ringCount = 5;
      const ringSpacing = maxRadius / ringCount;

      ctx.lineWidth = 1;
      for (let i = 1; i <= ringCount; i++) {
        const r = ringSpacing * i;
        ctx.strokeStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(origin.x, origin.y, r, 0, Math.PI * 2);
        ctx.stroke();

        if (i % 2 === 0) {
          ctx.save();
          ctx.translate(origin.x, origin.y);
          for (let deg = 0; deg < 360; deg += 10) {
            const angle = (deg * Math.PI) / 180;
            const inner = r - 5;
            const outer = r + 5;
            const x1 = Math.cos(angle) * inner;
            const y1 = Math.sin(angle) * inner;
            const x2 = Math.cos(angle) * outer;
            const y2 = Math.sin(angle) * outer;

            ctx.beginPath();
            ctx.moveTo(x1, -y1);
            ctx.lineTo(x2, -y2);
            ctx.stroke();
          }
          ctx.restore();
        }
      }

      formations.forEach((f) => {
        f.angle += f.speed;
        const leaderX = f.center.x + f.radius * Math.cos(f.angle);
        const leaderY = f.center.y + f.radius * Math.sin(f.angle);
        const dirX = -Math.sin(f.angle);
        const dirY = Math.cos(f.angle);

        f.units.forEach((unit) => {
          const offsetX = unit.x * dirY - unit.y * dirX;
          const offsetY = unit.x * -dirX - unit.y * dirY;

          const x = leaderX + offsetX;
          const y = leaderY + offsetY;

          ctx.beginPath();
          ctx.arc(x, y, 3, 0, Math.PI * 2);
          ctx.fillStyle = f.color;
          ctx.fill();

          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + dirX * 6, y + dirY * 6);
          ctx.strokeStyle = f.color;
          ctx.lineWidth = 2;
          ctx.stroke();
        });
      });

      requestAnimationFrame(draw);
    };

    draw();
    return () => window.removeEventListener("resize", resize);
  }, []);

  return <canvas ref={canvasRef} className={styles.background} />;
};

export default Background;
